import { IUserContextModel } from "@/store/auth/types";
import { ComponentKeys, UpdatedUserRole } from "../enums/enums";
import { getComponentValueSafe } from "./componentListHelper";
import { isActiveWithContext } from "../utils/utils";

/**
 * Check if integration settings should be visible based on active tenant components
 * @param path - The route path to check
 * @returns boolean - true if integration settings should be visible
 */
export const integrationSettingsPaths = [
  "/integration-settings",
  "/integration-settings/roaming",
  "/integration-settings/roaming-hubject",
  "/integration-settings/refunding",
  "/integration-settings/pricing",
  "/integration-settings/billing",
  "/integration-settings/analytics",
  "/integration-settings/smartCharging",
  "/integration-settings/carConnector",
  "/integration-settings/asset",
];

export const isIntegrationSettingsVisibleWithContext = (
  path: string | undefined,
  authContext: IUserContextModel
): boolean => {
  const userRole = authContext?.userInfo?.role as UpdatedUserRole;
  const isVisible = [UpdatedUserRole.CPO_ADMIN, UpdatedUserRole.ADMIN].includes(
    userRole
  );
  //  If path is provided and it's not an integration settings path, return true (not our concern)
  if (
    path &&
    !integrationSettingsPaths.some((settingsPath) =>
      path.startsWith(settingsPath)
    ) &&
    isVisible
  ) {
    return true;
  }

  // Check all component activations using the hook-free version (dynamic component list)
  const isAnalyticsComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.ANALYTICS),
    authContext
  );
  const isAssetComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.ASSET),
    authContext
  );
  const isRoamingComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.OCPI),
    authContext
  );
  const isRoamingHubjectComponent = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.OICP),
    authContext
  );
  const isBillingComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.BILLING),
    authContext
  );
  const isCarConnectorComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.CAR_CONNECTOR),
    authContext
  );
  const isPricingComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.PRICING),
    authContext
  );
  const isRefundingComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.REFUND),
    authContext
  );
  const isSmartChargingComponentActive = isActiveWithContext(
    getComponentValueSafe(ComponentKeys.SMART_PROFILE),
    authContext
  );

  // If specific path is provided, check component for that specific path
  if (path) {
    switch (path) {
      case "/integration-settings/analytics":
        return isAnalyticsComponentActive;
      case "/integration-settings/asset":
        return isAssetComponentActive;
      case "/integration-settings/roaming":
        return isRoamingComponentActive;
      case "/integration-settings/roaming-hubject":
        return isRoamingHubjectComponent;
      case "/integration-settings/billing":
        return isBillingComponentActive;
      case "/integration-settings/carConnector":
        return isCarConnectorComponentActive;
      case "/integration-settings/pricing":
        return isPricingComponentActive;
      case "/integration-settings/refunding":
        return isRefundingComponentActive;
      case "/integration-settings/smartCharging":
        return isSmartChargingComponentActive;
      case "/integration-settings":
        // For main integration settings path, check if any component is active
        break;
      default:
        // For any other integration settings sub-path, check if any component is active
        break;
    }
  }

  // Return true if ANY of the integration components is active
  return (
    isVisible &&
    (isAnalyticsComponentActive ||
      isAssetComponentActive ||
      isRoamingComponentActive ||
      isRoamingHubjectComponent ||
      isBillingComponentActive ||
      isCarConnectorComponentActive ||
      isPricingComponentActive ||
      isRefundingComponentActive ||
      isSmartChargingComponentActive)
  );
};
